import { Suspense, useMemo } from "react";
import { Toaster } from "@edusogno/primitives/toast";
import { SeoProvider } from "@/contexts/SeoContext.tsx";
import { ModalProvider } from "@/contexts/modal/Context.tsx";
import { SnackbarProvider } from "notistack";
import { ExerciseProvider } from "@/contexts/exercise/Context";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MixpanelTrackingProvider } from "./mixpanel/MixpanelTrackingContext";
import { captureException, withScope } from "@sentry/react";
import { validateEnvironmentVariables } from "@/config/validate-environment-variables";
import {
  QueryCache,
  QueryClient,
  MutationCache,
  QueryClientProvider,
} from "@tanstack/react-query";
import "@/utils/i18n";
import "@edusogno/ui/css";
import Intercom from "@/components/Intercom.tsx";
import AllRoutes from "@/routes";
import PageLoader from "@edusogno/composites/page-loader";
import MoreProvider from "./contexts/MoreContext";
import ModalsContainer from "@/contexts/modal/Container.tsx";
import { TimezoneProvider } from "./contexts/TimezoneContext";

validateEnvironmentVariables();

function EdusognoApp() {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        mutationCache: new MutationCache({
          onError: (err, _variables, _context, mutation) => {
            withScope((scope) => {
              scope.setContext("mutation", {
                mutationId: mutation.mutationId,
                variables: mutation.state.variables,
              });

              if (mutation.options.mutationKey) {
                scope.setFingerprint(
                  // Duplicate to prevent modification
                  Array.from(mutation.options.mutationKey) as string[],
                );
              }

              captureException(err);
            });
          },
        }),
        queryCache: new QueryCache({
          onError: (err, query) => {
            withScope((scope) => {
              scope.setContext("query", { queryHash: query.queryHash });

              scope.setFingerprint([
                query.queryHash.replaceAll(/[0-9]/g, "0"),
              ] as string[]);

              captureException(err);
            });
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 30_000,
          },
        },
      }),
    [],
  );

  return (
    <MixpanelTrackingProvider>
      <MoreProvider>
        <QueryClientProvider client={queryClient}>
          <TimezoneProvider>
            <ExerciseProvider>
              <ModalProvider>
                <SnackbarProvider
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <SeoProvider>
                    <Suspense fallback={<PageLoader />}>
                      <AllRoutes />

                      <Intercom />
                    </Suspense>
                  </SeoProvider>
                </SnackbarProvider>

                <ModalsContainer />
              </ModalProvider>
            </ExerciseProvider>

            <Toaster />

            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          </TimezoneProvider>
        </QueryClientProvider>
      </MoreProvider>
    </MixpanelTrackingProvider>
  );
}

export default EdusognoApp;
